import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTenant } from "../../context/TenantContext";
import ConsultationListSection from "./ConsultationListSection";
import EvaluationFormListSection from "./EvaluationFormListSection";

function withAllCliniciansLabel(rolePlural, list) {
  return [
    { value: null, label: `All ${rolePlural}` },
    ...(list || []).map((d) => ({
      value: String(d.id),
      label: d.name,
    })),
  ];
}

const emptyAlliedStaff = () => ({
  special_educator: [],
  occupational_therapist: [],
  speech_therapist: [],
  rehabilitation_psychologist: [],
  pediatric_physiotherapist: [],
});

const GenerateNotesPage = () => {
  const { patientService, tenant } = useTenant();

  const [psychiatrists, setPsychiatrists] = useState([]);
  const [psychologists, setPsychologists] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorsError, setDoctorsError] = useState(null);

  const [alliedStaff, setAlliedStaff] = useState(emptyAlliedStaff);
  const [alliedLoading, setAlliedLoading] = useState(false);

  const showAlliedSessionTabs =
    tenant?.id === "mindtalk" &&
    tenant?.features?.generateNotesAlliedSessionTabs === true;

  const [mainTab, setMainTab] = useState(0);

  const fetchPsychAndPsychologistLists = useCallback(async () => {
    try {
      setDoctorsLoading(true);
      setDoctorsError(null);
      const [psychiatristsRes, psychologistsRes] = await Promise.all([
        patientService.fetchPsychiatrists(),
        patientService.fetchClinicalPsychologists(),
      ]);
      setPsychiatrists(psychiatristsRes || []);
      setPsychologists(psychologistsRes || []);
    } catch (err) {
      console.error("Failed to fetch doctors:", err);
      setDoctorsError(err?.message || "Failed to fetch doctors");
      setPsychiatrists([]);
      setPsychologists([]);
    } finally {
      setDoctorsLoading(false);
    }
  }, [patientService]);

  const fetchAlliedStaffLists = useCallback(async () => {
    try {
      setAlliedLoading(true);
      const [
        specialEducatorsRes,
        occupationalRes,
        speechRes,
        rehabRes,
        pediatricRes,
      ] = await Promise.all([
        patientService.fetchSpecialEducators(),
        patientService.fetchOccupationalTherapists(),
        patientService.fetchSpeechTherapists(),
        patientService.fetchRehabilitationPsychologists(),
        patientService.fetchPediatricPhysiotherapists(),
      ]);
      setAlliedStaff({
        special_educator: specialEducatorsRes || [],
        occupational_therapist: occupationalRes || [],
        speech_therapist: speechRes || [],
        rehabilitation_psychologist: rehabRes || [],
        pediatric_physiotherapist: pediatricRes || [],
      });
    } catch (err) {
      console.error("Failed to fetch allied clinician lists:", err);
      setAlliedStaff(emptyAlliedStaff());
    } finally {
      setAlliedLoading(false);
    }
  }, [patientService]);

  useEffect(() => {
    fetchPsychAndPsychologistLists();
    if (showAlliedSessionTabs) {
      fetchAlliedStaffLists();
    } else {
      setAlliedStaff(emptyAlliedStaff());
    }
    setMainTab(0);
  }, [
    tenant?.id,
    showAlliedSessionTabs,
    fetchPsychAndPsychologistLists,
    fetchAlliedStaffLists,
  ]);

  const psychiatristListDoctorOptions = useMemo(() => {
    return withAllCliniciansLabel("psychiatrists", psychiatrists);
  }, [psychiatrists]);

  const psychologistListDoctorOptions = useMemo(() => {
    return withAllCliniciansLabel("psychologists", psychologists);
  }, [psychologists]);

  const specialEducatorOptions = useMemo(
    () => withAllCliniciansLabel("special educators", alliedStaff.special_educator),
    [alliedStaff.special_educator],
  );
  const occupationalTherapistOptions = useMemo(
    () =>
      withAllCliniciansLabel(
        "occupational therapists",
        alliedStaff.occupational_therapist,
      ),
    [alliedStaff.occupational_therapist],
  );
  const speechTherapistOptions = useMemo(
    () => withAllCliniciansLabel("speech therapists", alliedStaff.speech_therapist),
    [alliedStaff.speech_therapist],
  );
  const rehabilitationPsychologistOptions = useMemo(
    () =>
      withAllCliniciansLabel(
        "rehabilitation psychologists",
        alliedStaff.rehabilitation_psychologist,
      ),
    [alliedStaff.rehabilitation_psychologist],
  );
  const pediatricPhysioOptions = useMemo(
    () =>
      withAllCliniciansLabel(
        "pediatric physiotherapists",
        alliedStaff.pediatric_physiotherapist,
      ),
    [alliedStaff.pediatric_physiotherapist],
  );

  const alliedOptionsByKind = useMemo(
    () => ({
      special_educator: specialEducatorOptions,
      occupational_therapist: occupationalTherapistOptions,
      speech_therapist: speechTherapistOptions,
      rehabilitation_psychologist: rehabilitationPsychologistOptions,
      pediatric_physiotherapist: pediatricPhysioOptions,
    }),
    [
      specialEducatorOptions,
      occupationalTherapistOptions,
      speechTherapistOptions,
      rehabilitationPsychologistOptions,
      pediatricPhysioOptions,
    ],
  );

  const generateNotesTabs = useMemo(() => {
    const rows = [
      {
        key: "psychiatrist",
        type: "consultation",
        kind: "psychiatrist",
        label: "Psychiatrist consultations",
        doctorOptions: psychiatristListDoctorOptions,
      },
      {
        key: "psychologist",
        type: "consultation",
        kind: "psychologist",
        label: "Psychologist consultations",
        doctorOptions: psychologistListDoctorOptions,
      },
    ];

    if (showAlliedSessionTabs) {
      const alliedDefs = [
        { kind: "special_educator", label: "Special educator sessions" },
        { kind: "occupational_therapist", label: "Occupational therapist sessions" },
        { kind: "speech_therapist", label: "Speech therapist sessions" },
        {
          kind: "rehabilitation_psychologist",
          label: "Rehabilitation psychologist sessions",
        },
        {
          kind: "pediatric_physiotherapist",
          label: "Pediatric physiotherapist sessions",
        },
      ];
      for (const def of alliedDefs) {
        rows.push({
          key: def.kind,
          type: "consultation",
          kind: def.kind,
          label: def.label,
          doctorOptions: alliedOptionsByKind[def.kind] || [
            { value: null, label: "All clinicians" },
          ],
        });
      }
    }

    rows.push({
      key: "evaluation_forms",
      type: "evaluation",
      label: "Evaluation forms",
    });

    return rows;
  }, [
    showAlliedSessionTabs,
    psychiatristListDoctorOptions,
    psychologistListDoctorOptions,
    alliedOptionsByKind,
  ]);

  const activeTab = generateNotesTabs[mainTab] ?? generateNotesTabs[0];
  const listDoctorsLoading = doctorsLoading || (showAlliedSessionTabs && alliedLoading);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ md: "flex-end" }}
        spacing={1}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 0.25 }}>
            Consultations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse generated notes across consultations, sessions, and evaluation forms for{" "}
            <strong>{tenant?.label || tenant?.id}</strong>.
          </Typography>
        </Box>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          {generateNotesTabs.map((tab, i) => (
            <Tab key={tab.key} label={tab.label} value={i} />
          ))}
        </Tabs>
      </Paper>

      {activeTab?.type === "consultation" && (
        <>
          {listDoctorsLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Loading clinician lists for filters…
            </Typography>
          )}
          {doctorsError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Doctor list error: {doctorsError}
            </Typography>
          )}
          {/*
            Keyed on kind + campus so the section remounts with empty filters when either
            changes. Without this the instance is reused and a stale `doctorId` from the
            previous tab/campus is still sent — the dropdown renders blank while the list
            silently returns nothing.
          */}
          <ConsultationListSection
            key={`${activeTab.kind}:${tenant?.id}`}
            kind={activeTab.kind}
            patientService={patientService}
            doctorOptions={activeTab.doctorOptions}
            psychiatrists={psychiatrists}
            psychologists={psychologists}
          />
        </>
      )}

      {activeTab?.type === "evaluation" && (
        <>
          {listDoctorsLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Loading clinician lists for filters…
            </Typography>
          )}
          {doctorsError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Doctor list error: {doctorsError}
            </Typography>
          )}
          <EvaluationFormListSection
            key={`evaluation:${tenant?.id}`}
            patientService={patientService}
            doctorOptions={psychiatristListDoctorOptions}
            psychiatrists={psychiatrists}
            psychologists={psychologists}
          />
        </>
      )}
    </Box>
  );
};

export default GenerateNotesPage;
export { ALLIED_SESSION_KINDS } from "./ConsultationListSection";
